'use client';

import { FormProvider } from 'react-hook-form';
import { HeroArea } from '../HeroArea';
import { Box, Fade, List, Pagination, Typography } from '@mui/material';
import { LocationIcon } from '@/features/icon/LocationIcon';
import {
  api_portalOfficeListing_filter,
  api_portalOfficeHospitalListing_medicalSubject,
  api_portalOfficeHospitalListing_reportedAdditionalFee,
  api_portalOfficeHospitalListing_responseFilter,
} from '@/init/swagger';
import { useLogic } from './logic';
import { Loading } from '@/components/loader/Loading';
import { CustomPalettes } from '@/themes';
import { MuiIconButton } from '@/components/button/MuiIconButton';
import { ChevronUp } from 'mdi-material-ui';
import { useLayoutFooter } from '@/hooks/useLayoutFooter';
import { useState, useEffect } from 'react';
import { PublicHomeSearchResults } from '../PublicHomeSearchResults';
import { PublicHomeHospitalSearchFilterBox } from '../PublicHomeHospitalSearchFilterBox';
import { SearchAppHomeParamsOutput } from '@/helpers/searchAppHomeHelper';
import { getOfficialAnnouncementListInfo } from '@/types/officialAnnouncement';
import { EmptyBox } from '@/components/EmptyBox';
import { OfficialAnnouncementListItem } from './OfficialAnnouncementListItem';
import { PublicHomeLatestPortalOfficeListingList } from './PublicHomeLatestPortalOfficeListingList';
import { PublicHomeSearchFilterBox } from '../PublicHomeSearchFilterBox';

type Props = {
  searchFilters: api_portalOfficeListing_filter[];
  hospitalSearchFilters: api_portalOfficeHospitalListing_responseFilter[];
  hospitalMedicalSubjects: api_portalOfficeHospitalListing_medicalSubject[];
  hospitalReportedAdditionalFees: api_portalOfficeHospitalListing_reportedAdditionalFee[];
  validatedSearchParams: SearchAppHomeParamsOutput;
  officialAnnouncementListInfo: ReturnType<
    typeof getOfficialAnnouncementListInfo
  >;
};

export function PublicHomeSearchForm({
  searchFilters,
  hospitalSearchFilters,
  hospitalMedicalSubjects,
  hospitalReportedAdditionalFees,
  validatedSearchParams,
  officialAnnouncementListInfo,
}: Props) {
  const {
    isPending,
    method,
    search,
    onChangePagination,
    isLoading,
    paginationInfo,
    targetAddress,
    portalOfficeListings,
    latestPortalOfficeListings,
  } = useLogic({
    validatedSearchParams,
    searchFilters,
    hospitalSearchFilters,
  });

  const { footerHeight } = useLayoutFooter();

  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <FormProvider {...method}>
      <Loading isLoading={isPending || isLoading} />
      <HeroArea search={search} isPublic={true} />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '280px auto 240px',
          gap: 2,
          mt: 8,
          '@media (max-width: 1199px)': {
            display: 'grid',
            gridTemplateColumns: '280px auto',
          },
          '@media (max-width: 999px)': {
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <PublicHomeSearchFilterBox
            search={search}
            searchFilters={searchFilters}
          />

          <PublicHomeHospitalSearchFilterBox
            search={search}
            hospitalMedicalSubjects={hospitalMedicalSubjects}
            hospitalReportedAdditionalFees={hospitalReportedAdditionalFees}
          />
        </Box>

        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 2,
              mb: 4,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <LocationIcon color="default" width={20} height={20} />
              <Typography
                variant="JP/Body/m/Regular/Normal"
                color="text.secondary"
                component="p"
              >
                {targetAddress}
              </Typography>
            </Box>
            <Typography
              variant="JP/Heading/lg/Bold/Tight"
              component="p"
              sx={{
                mb: 2,
                '@media (max-width: 600px)': { fontSize: '20px' },
              }}
            >
              周辺の看護・介護サービス・医療機関
            </Typography>

            <PublicHomeSearchResults
              portalOfficeListings={portalOfficeListings}
              searchFilters={searchFilters}
              hospitalMedicalSubjects={hospitalMedicalSubjects}
            />

            <Pagination
              count={paginationInfo.lastPage}
              page={paginationInfo.currentPage}
              onChange={onChangePagination}
              siblingCount={2}
              boundaryCount={0}
              variant="outlined"
              shape="rounded"
              showFirstButton
              showLastButton
              sx={{
                margin: '12px auto 24px auto',
                '.MuiPaginationItem-root': {
                  backgroundColor: CustomPalettes['Space/pallet/Gray/0'],
                },
                '.MuiPaginationItem-root.Mui-selected': {
                  backgroundColor:
                    CustomPalettes['Space/pallet/Primary/500-primary'],
                  color: CustomPalettes['Space/pallet/Gray/0'],
                },
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            '@media (max-width: 1199px)': {
              gridColumn: '1 / 3',
              flexDirection: 'row',
              mt: 4,
            },
            '@media (max-width: 999px)': {
              mt: 2,
            },
            '@media (max-width: 600px)': {
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            },
          }}
        >
          <Box
            sx={{
              padding: '24px',
              paddingBottom: '8px',
              background: '#FAFAFA',
              borderRadius: '4px',
              py: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 2,
              }}
            >
              <Typography variant="JP/Heading/s/Bold/Tight" component="p">
                お知らせ
              </Typography>
            </Box>

            {officialAnnouncementListInfo.length > 0 ? (
              <List>
                {officialAnnouncementListInfo.map(
                  (officialAnnouncementInfo, index) => (
                    <OfficialAnnouncementListItem
                      key={index}
                      officialAnnouncementInfo={officialAnnouncementInfo}
                    />
                  ),
                )}
              </List>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <EmptyBox mainMessage="通知がありません" />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {!!latestPortalOfficeListings?.length && (
        <Box
          sx={{
            mt: 10,
            '@media (max-width: 1199px)': {
              mt: 6,
            },
          }}
        >
          <Typography
            variant="JP/Heading/m/Bold/Tight"
            component="p"
            sx={{ mb: 4, '@media (max-width: 600px)': { fontSize: '20px' } }}
          >
            最近更新された看護・介護サービス・医療機関
          </Typography>

          <PublicHomeLatestPortalOfficeListingList
            portalOfficeListings={latestPortalOfficeListings}
          />
        </Box>
      )}

      <Fade in={showScrollTop}>
        <MuiIconButton
          onClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          size="small"
          sx={{
            position: 'fixed',
            bottom: `${footerHeight + 20}px`,
            right: '24px',
            lineHeight: 1,
            zIndex: 99,
            bgcolor: 'text.disabled',
            color: 'white',
            '&:hover': {
              bgcolor: `rgba(0, 0, 0, 0.175)`,
            },
          }}
        >
          <ChevronUp fontSize="large" />
        </MuiIconButton>
      </Fade>
    </FormProvider>
  );
}
